/**
 * Detailed live quote display.
 *
 * Renders price, day-change, bid/ask + spread, last trade, previous close,
 * and a connection-status indicator. Updates in real time as ticks arrive
 * from the /ws/quotes websocket.
 */

import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  TrendingFlat as TrendingFlatIcon,
  WifiTetheringOff as DisconnectedIcon,
} from '@mui/icons-material';

import { Quote, StreamStatus } from '../../hooks/useQuoteStream';

interface Props {
  symbol: string | null;
  quote: Quote | null;
  status: StreamStatus;
}

const fmtMoney = (n: number | null | undefined, digits = 2): string =>
  n === null || n === undefined || Number.isNaN(n)
    ? '—'
    : n.toLocaleString('en-US', {
        minimumFractionDigits: digits,
        maximumFractionDigits: digits,
      });

const fmtSize = (n: number | null | undefined): string =>
  n === null || n === undefined || Number.isNaN(n) ? '—' : n.toLocaleString('en-US');

const QuoteCard: React.FC<Props> = ({ symbol, quote, status }) => {
  if (!symbol) {
    return (
      <Card variant="outlined">
        <CardContent>
          <Typography variant="body2" color="text.secondary">
            Enter a symbol to see a live quote.
          </Typography>
        </CardContent>
      </Card>
    );
  }

  const price = quote?.price ?? quote?.last ?? null;
  const change = quote?.change ?? null;
  const changePct = quote?.changePercent ?? null;

  const positive = change !== null && change > 0;
  const negative = change !== null && change < 0;
  const flat = change !== null && change === 0;

  const trendColor = positive ? 'success.main' : negative ? 'error.main' : 'text.secondary';
  const TrendIcon = positive ? TrendingUpIcon : negative ? TrendingDownIcon : TrendingFlatIcon;

  // Bid/ask spread (only shown when both legs are present + non-zero).
  const spread =
    quote?.bid && quote?.ask && quote.bid > 0 && quote.ask > 0
      ? quote.ask - quote.bid
      : null;
  const spreadPct =
    spread !== null && quote?.bid ? (spread / quote.bid) * 100 : null;

  return (
    <Card variant="outlined">
      <CardContent>
        {/* Header row: symbol + status pill */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, letterSpacing: 0.5 }}>
            {symbol.toUpperCase()}
          </Typography>
          <StatusChip status={status} />
        </Box>

        {/* Price + day change */}
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1.5, mb: 0.5 }}>
          {price === null ? (
            <Skeleton variant="text" width={120} sx={{ fontSize: '2rem' }} />
          ) : (
            <Typography variant="h4" sx={{ fontWeight: 600, lineHeight: 1.1 }}>
              ${fmtMoney(price)}
            </Typography>
          )}
          {(change !== null || changePct !== null) && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                color: trendColor,
              }}
            >
              <TrendIcon fontSize="small" />
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {change !== null ? `${change >= 0 ? '+' : ''}${fmtMoney(change)}` : ''}
                {changePct !== null
                  ? ` (${changePct >= 0 ? '+' : ''}${fmtMoney(changePct)}%)`
                  : ''}
              </Typography>
            </Box>
          )}
        </Box>

        <Typography variant="caption" color="text.secondary">
          {quote?.tradeTimestamp
            ? `last trade: ${new Date(quote.tradeTimestamp).toLocaleTimeString()}`
            : status === 'open'
            ? 'waiting for first tick…'
            : 'no data'}
        </Typography>

        <Divider sx={{ my: 1.5 }} />

        {/* Bid / Ask / Spread */}
        <Stack direction="row" spacing={2} sx={{ mb: 1.5 }}>
          <Box sx={{ flex: 1 }}>
            <Typography variant="caption" color="text.secondary" display="block">
              BID
            </Typography>
            <Typography variant="body1" sx={{ fontWeight: 600 }}>
              ${fmtMoney(quote?.bid)}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              size {fmtSize(quote?.bidSize)}
            </Typography>
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography variant="caption" color="text.secondary" display="block">
              ASK
            </Typography>
            <Typography variant="body1" sx={{ fontWeight: 600 }}>
              ${fmtMoney(quote?.ask)}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              size {fmtSize(quote?.askSize)}
            </Typography>
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography variant="caption" color="text.secondary" display="block">
              SPREAD
            </Typography>
            <Typography variant="body1" sx={{ fontWeight: 600 }}>
              {spread !== null ? `$${fmtMoney(spread)}` : '—'}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {spreadPct !== null ? `${fmtMoney(spreadPct)}%` : '—'}
            </Typography>
          </Box>
        </Stack>

        {/* Prev close + last trade size */}
        <Stack direction="row" spacing={2}>
          <Box sx={{ flex: 1 }}>
            <Typography variant="caption" color="text.secondary" display="block">
              PREV CLOSE
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              ${fmtMoney(quote?.previousClose)}
            </Typography>
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography variant="caption" color="text.secondary" display="block">
              LAST TRADE
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              ${fmtMoney(quote?.last)}
            </Typography>
          </Box>
        </Stack>

        {flat && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            Unchanged on the day.
          </Typography>
        )}
      </CardContent>
    </Card>
  );
};

const StatusChip: React.FC<{ status: StreamStatus }> = ({ status }) => {
  switch (status) {
    case 'open':
      return (
        <Chip
          size="small"
          color="success"
          label="LIVE"
          sx={{ fontWeight: 600, letterSpacing: 0.5 }}
        />
      );
    case 'connecting':
      return (
        <Chip
          size="small"
          icon={<CircularProgress size={12} sx={{ color: 'inherit' }} />}
          label="connecting"
          variant="outlined"
        />
      );
    case 'error':
    case 'closed':
      return (
        <Chip
          size="small"
          icon={<DisconnectedIcon fontSize="small" />}
          label="offline"
          color="warning"
          variant="outlined"
        />
      );
    default:
      return null;
  }
};

export default QuoteCard;
